import React, { useCallback, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { Button } from "antd";
import { Sparkle, ArrowRight, ArrowsClockwise } from "@phosphor-icons/react";
import {
  fetchDocumentBriefing,
  selectBriefingForDocument,
  selectIsAnalysisRunningForDocument,
} from "../../slice/AISlice";
import CategoryTag from "../CategoryTag";
import {
  AIPaneScrollBody,
  BriefingCategoryRow,
  BriefingTimestamp,
  BriefingDivider,
  RunDeepAnalysisLink,
  BriefingSkeletonBlock,
  BriefingSkeletonGroup,
  AnalysisEmptyDescription,
  AnalysisEmptyTitle,
  AnalysisEmptyWrapper,
  AnalysisEmptyIconRing,
} from "./AIPaneStyled";
import BriefingMarkdown from "./BriefingMarkdown";
import { handleCitationClick } from "../../utils/citationJump";

/**
 * Convert an ISO timestamp into a friendly relative label
 * ("just now", "2 hrs ago", "3 days ago"). Intentionally
 * lightweight — we render this on the fly, not in a tooltip,
 * so we avoid pulling in dayjs/relative-time just for this.
 */
const formatRelativeTimestamp = (isoString) => {
  if (!isoString) return null;
  const ts = Date.parse(isoString);
  if (Number.isNaN(ts)) return null;
  const deltaMs = Date.now() - ts;
  if (deltaMs < 0) return "just now";

  const minutes = Math.floor(deltaMs / 60000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes} min${minutes === 1 ? "" : "s"} ago`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hr${hours === 1 ? "" : "s"} ago`;

  const days = Math.floor(hours / 24);
  if (days < 30) return `${days} day${days === 1 ? "" : "s"} ago`;

  // Anything older than ~1mo we just show the date in a stable locale-free
  // form so long-lived briefings don't drift to "62 days ago".
  return new Date(ts).toISOString().slice(0, 10);
};

const BriefingTab = ({ documentId, onSwitchTab, isActive }) => {
  const dispatch = useDispatch();
  const briefingState = useSelector((state) =>
    selectBriefingForDocument(state, documentId)
  );
  const isAnalysisRunning = useSelector((state) =>
    selectIsAnalysisRunningForDocument(state, documentId)
  );

  // AIPane keeps all three tab bodies mounted to preserve chat
  // conversation state, so this component does NOT remount on tab
  // switches. The fetch logic balances three requirements:
  //
  //   1. `idle` always fetches. Covers the fresh-mount path AND the
  //      post-`clearDocumentAIState` reopen of a closed doc, where
  //      the slice has dropped the entry back to `idle`.
  //   2. `failed` / `empty` retry once per documentId. The dedupe
  //      ref tracks the last documentId we dispatched a retry for;
  //      a same-doc tab toggle (Briefing → Analysis → Briefing) does
  //      NOT re-fetch — the user must click the Retry / "Check again"
  //      button. A document switch (A → B → A) DOES re-fetch on the
  //      return because the ref now holds B; this is intentional
  //      since coming back to a doc is a natural retry point.
  //   3. `loading` / `ready` are no-ops.
  //
  // We deliberately do NOT include `briefingState.status` in the dep
  // array because a 404 transitions to `empty`, which would re-fire
  // the retry-eligible check and hit the backend in a loop.
  const lastFetchedDocIdRef = useRef(null);
  useEffect(() => {
    if (!documentId || !isActive) return;
    if (briefingState.status === "idle") {
      // No cache entry (fresh mount OR cleared via clearDocumentAIState).
      // Always fetch — the ref isn't a valid dedupe signal here.
      lastFetchedDocIdRef.current = documentId;
      dispatch(fetchDocumentBriefing(documentId));
      return;
    }
    if (
      (briefingState.status === "failed" || briefingState.status === "empty") &&
      lastFetchedDocIdRef.current !== documentId
    ) {
      lastFetchedDocIdRef.current = documentId;
      dispatch(fetchDocumentBriefing(documentId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, documentId, isActive]);

  const handleRetry = useCallback(() => {
    if (!documentId) return;
    lastFetchedDocIdRef.current = documentId;
    dispatch(fetchDocumentBriefing(documentId));
  }, [dispatch, documentId]);

  if (!documentId) {
    return (
      <AnalysisEmptyWrapper>
        <AnalysisEmptyIconRing>
          <Sparkle size={32} weight="duotone" />
        </AnalysisEmptyIconRing>
        <AnalysisEmptyTitle>No document selected</AnalysisEmptyTitle>
        <AnalysisEmptyDescription>
          Open a document from the file tree to see its AI-generated briefing.
        </AnalysisEmptyDescription>
      </AnalysisEmptyWrapper>
    );
  }

  if (briefingState.status === "loading" || briefingState.status === "idle") {
    return (
      <AIPaneScrollBody>
        <BriefingSkeletonGroup>
          <BriefingSkeletonBlock $w="35%" $h={20} />
          <BriefingSkeletonBlock $w="100%" $h={14} />
          <BriefingSkeletonBlock $w="92%" $h={14} />
          <BriefingSkeletonBlock $w="78%" $h={14} />
          <BriefingSkeletonBlock $w="60%" $h={20} />
          <BriefingSkeletonBlock $w="100%" $h={14} />
          <BriefingSkeletonBlock $w="88%" $h={14} />
        </BriefingSkeletonGroup>
      </AIPaneScrollBody>
    );
  }

  if (briefingState.status === "failed") {
    return (
      <AnalysisEmptyWrapper>
        <AnalysisEmptyTitle>Couldn&apos;t load briefing</AnalysisEmptyTitle>
        <AnalysisEmptyDescription>
          {briefingState.error ||
            "Something went wrong fetching the AI briefing."}
        </AnalysisEmptyDescription>
        <Button
          icon={<ArrowsClockwise size={14} weight="bold" />}
          onClick={handleRetry}
        >
          Retry
        </Button>
      </AnalysisEmptyWrapper>
    );
  }

  if (briefingState.status === "empty" || !briefingState.data) {
    return (
      <AnalysisEmptyWrapper>
        <AnalysisEmptyIconRing>
          <Sparkle size={32} weight="duotone" />
        </AnalysisEmptyIconRing>
        <AnalysisEmptyTitle>AI briefing not ready yet</AnalysisEmptyTitle>
        <AnalysisEmptyDescription>
          The pipeline writes a briefing automatically once this document
          finishes processing. Check back in a few minutes.
        </AnalysisEmptyDescription>
        <Button
          icon={<ArrowsClockwise size={14} weight="bold" />}
          onClick={handleRetry}
        >
          Check again
        </Button>
      </AnalysisEmptyWrapper>
    );
  }

  const briefing = briefingState.data;
  const relativeTimestamp = formatRelativeTimestamp(
    briefing.created_at || briefing.updated_at
  );

  return (
    <AIPaneScrollBody>
      <BriefingCategoryRow>
        <CategoryTag category={briefing.category || "Document"} />
        {relativeTimestamp ? (
          <BriefingTimestamp>Generated {relativeTimestamp}</BriefingTimestamp>
        ) : null}
      </BriefingCategoryRow>

      <BriefingMarkdown
        content={briefing.briefing_text || ""}
        onCitationClick={(parsed) => handleCitationClick(parsed, documentId)}
      />

      <BriefingDivider />

      <RunDeepAnalysisLink
        type="button"
        onClick={() => onSwitchTab && onSwitchTab("analysis")}
        disabled={isAnalysisRunning}
        aria-label={
          isAnalysisRunning
            ? "Deep analysis already running"
            : "Run deep AI analysis on this document"
        }
      >
        {isAnalysisRunning ? "Analysis in progress…" : "Run deep analysis"}
        {!isAnalysisRunning && <ArrowRight size={14} weight="bold" aria-hidden="true" />}
      </RunDeepAnalysisLink>
    </AIPaneScrollBody>
  );
};

BriefingTab.propTypes = {
  documentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onSwitchTab: PropTypes.func,
  isActive: PropTypes.bool,
};

BriefingTab.defaultProps = {
  documentId: null,
  onSwitchTab: undefined,
  isActive: true,
};

export default BriefingTab;
